import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react'
import type { Socket } from 'socket.io-client'
import { useAuth } from '@contexts/AuthContext'
import { getOfficeSocket, tearDownOfficeSocket } from '@pages/Office/socketClient'
import { dedupePresenceByUser } from '@pages/Office/presenceUtils'

export type OfficeLocation = 'office' | 'app'

export interface OfficePresenceUser {
  socketId: string
  userId: string
  name: string
  avatar: string | null
  role: string
  location: OfficeLocation
}

// ── Context A：穩定的 socket 控制面（socket / isConnected / setMyLocation）─────
// 這個 context 只在 socket 連線狀態改變時才 re-render，不隨每次 presence 事件更新
interface OfficeSocketContextValue {
  socket: Socket | null
  isConnected: boolean
  setMyLocation: (loc: OfficeLocation) => void
}

const SocketCtx = createContext<OfficeSocketContextValue | null>(null)

// ── Context B：高頻率更新的 presence 名單 ────────────────────────────────────
// 每次 join/leave/locationChange 事件都會更新，拆開讓只顯示線上人員的元件
// 訂閱此 context，不影響其他只需要 socket 控制的元件
const PresenceCtx = createContext<OfficePresenceUser[]>([])

// ── 向後相容的 combined context（維持舊 useOfficePresence() API）────────────
interface OfficePresenceContextValue extends OfficeSocketContextValue {
  presence: OfficePresenceUser[]
}

// Connects the office socket as soon as a user is authenticated and keeps it
// alive across pages. /office uses the same singleton; OfficePresenceContext
// merely owns the lifecycle and listens for the snapshot/join/leave events so
// any page can show "who's online" UI.
export function OfficePresenceProvider({ children }: { children: ReactNode }) {
  const { user, token } = useAuth()
  const [presence, setPresence] = useState<OfficePresenceUser[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [socket, setSocket] = useState<Socket | null>(null)

  useEffect(() => {
    if (!user || !token) {
      tearDownOfficeSocket()
      setSocket(null)
      setPresence([])
      setIsConnected(false)
      return
    }
    const s = getOfficeSocket()
    setSocket(s)
    if (!s.connected) s.connect()

    const onConnect = () => setIsConnected(true)
    const onDisconnect = () => setIsConnected(false)

    interface SnapshotPayload {
      me: OfficePresenceUser
      users: OfficePresenceUser[]
    }
    const onSnapshot = (snap: SnapshotPayload) => {
      setPresence(snap.users)
    }
    const onJoined = (u: OfficePresenceUser) => {
      setPresence((prev) => {
        if (prev.some((p) => p.socketId === u.socketId)) return prev
        return [...prev, u]
      })
    }
    const onLeft = (p: { socketId: string }) => {
      setPresence((prev) => prev.filter((u) => u.socketId !== p.socketId))
    }
    interface LocationChangePayload {
      socketId: string
      userId: string
      location: OfficeLocation
    }
    const onLocationChanged = (p: LocationChangePayload) => {
      setPresence((prev) => prev.map((u) => (u.socketId === p.socketId ? { ...u, location: p.location } : u)))
    }

    s.on('connect', onConnect)
    s.on('disconnect', onDisconnect)
    s.on('office:snapshot', onSnapshot)
    s.on('office:userJoined', onJoined)
    s.on('office:userLeft', onLeft)
    s.on('office:userLocationChanged', onLocationChanged)

    return () => {
      s.off('connect', onConnect)
      s.off('disconnect', onDisconnect)
      s.off('office:snapshot', onSnapshot)
      s.off('office:userJoined', onJoined)
      s.off('office:userLeft', onLeft)
      s.off('office:userLocationChanged', onLocationChanged)
    }
  }, [user, token])

  // setMyLocation 穩定化：不捕獲 socket state，每次都從 singleton 取
  const setMyLocation = useCallback((loc: OfficeLocation) => {
    // Fetch the current singleton each call instead of capturing socket
    // from useMemo deps — that way the emit works even if the consumer
    // called us before the provider's setSocket() re-render landed.
    getOfficeSocket().emit('office:setLocation', { location: loc })
  }, [])

  // SocketCtx value 只在 socket / isConnected 改變時更新
  // presence 刻意排除在外，避免每次 join/leave 都讓訂閱 socket 的元件 re-render
  const socketValue = useMemo<OfficeSocketContextValue>(
    () => ({ socket, isConnected, setMyLocation }),
    [socket, isConnected, setMyLocation],
  )

  // 伺服器是每條連線一筆；同一個人多開分頁時會重複，對外一律一人一筆
  const presenceByUser = useMemo(() => dedupePresenceByUser(presence), [presence])

  return (
    <SocketCtx.Provider value={socketValue}>
      <PresenceCtx.Provider value={presenceByUser}>
        {children}
      </PresenceCtx.Provider>
    </SocketCtx.Provider>
  )
}

// ── Hooks ──────────────────────────────────────────────────────────────────

/**
 * 向後相容 hook：回傳完整的 { socket, isConnected, setMyLocation, presence }
 * 既有呼叫端不需要改。
 * 注意：使用此 hook 的元件會在 presence 更新時 re-render。
 * 若元件只需要 socket 控制（不顯示人員清單），改用 useOfficeSocket()。
 */
export function useOfficePresence(): OfficePresenceContextValue {
  const socketCtx = useContext(SocketCtx)
  const presence = useContext(PresenceCtx)
  if (!socketCtx) throw new Error('useOfficePresence must be inside OfficePresenceProvider')
  // 合併兩個 context 回傳，讓既有呼叫端零修改
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ({ ...socketCtx, presence }), [socketCtx, presence])
}

/**
 * 窄 hook：只訂閱 socket 控制面（socket / isConnected / setMyLocation）。
 * presence 更新時此 hook 的消費者不會 re-render。
 */
export function useOfficeSocket(): OfficeSocketContextValue {
  const ctx = useContext(SocketCtx)
  if (!ctx) throw new Error('useOfficeSocket must be inside OfficePresenceProvider')
  return ctx
}

/**
 * 窄 hook：只訂閱在線人員清單。
 * 適合 OnlineColleaguesPanel 等需要頻繁更新但不需要 socket 控制的元件。
 */
export function useOfficePresenceList(): OfficePresenceUser[] {
  return useContext(PresenceCtx)
}
